<script setup>
import {
  ref,
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  useId,
  watch,
} from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  modelValue: {
    type: [String, Number],
    default: null,
  },
  options: {
    type: Array,
    required: true,
  },
  placeholder: {
    type: String,
    default: '',
  },
  disabled: {
    type: Boolean,
    default: false,
  },
  placement: {
    type: String,
    default: 'bottom-start',
    validator: v =>
      [
        'bottom-start',
        'bottom-end',
        'top-start',
        'top-end',
        'bottom',
        'top',
      ].includes(v),
  },
});

const emit = defineEmits(['update:modelValue', 'change']);

const { t } = useI18n();

const open = ref(false);
const pillRef = ref(null);
const triggerRef = ref(null);
const optionRefs = ref([]);
const uid = useId();
const triggerId = `inline-select-trigger-${uid}`;
const listboxId = `inline-select-listbox-${uid}`;

// ── Derived ──────────────────────────────────────────────────────────────────

const selectedOption = computed(
  () => props.options.find(o => o.value === props.modelValue) ?? null
);

const resolvedPlaceholder = computed(
  () => props.placeholder || t('PIPELINE.INLINE_EDIT.SELECT_HINT')
);

const pillLabel = computed(
  () => selectedOption.value?.label ?? resolvedPlaceholder.value
);

const pillColorClass = computed(
  () => selectedOption.value?.colorClass ?? 'bg-n-alpha-1 text-n-slate-11'
);

// ── Popover position ─────────────────────────────────────────────────────────

const popoverClass = computed(() => {
  const map = {
    'bottom-start': 'top-full left-0 mt-1',
    'bottom-end': 'top-full right-0 mt-1',
    bottom: 'top-full left-1/2 -translate-x-1/2 mt-1',
    'top-start': 'bottom-full left-0 mb-1',
    'top-end': 'bottom-full right-0 mb-1',
    top: 'bottom-full left-1/2 -translate-x-1/2 mb-1',
  };
  return map[props.placement] ?? map['bottom-start'];
});

// ── Interactions ─────────────────────────────────────────────────────────────

const enabledOptionElements = () =>
  optionRefs.value.filter((element, index) => {
    return element && !props.options[index]?.disabled;
  });

const focusOption = position => {
  const options = enabledOptionElements();
  if (!options.length) return;

  if (position === 'last') {
    options.at(-1)?.focus();
    return;
  }

  const selectedIndex = props.options.findIndex(
    option => option.value === props.modelValue && !option.disabled
  );
  const selectedElement = optionRefs.value[selectedIndex];
  (position === 'selected' && selectedElement
    ? selectedElement
    : options[0]
  )?.focus();
};

const openListbox = (focusPosition = 'selected') => {
  if (props.disabled || open.value) return;
  open.value = true;
  nextTick(() => focusOption(focusPosition));
};

const closeListbox = ({ restoreFocus = false } = {}) => {
  if (!open.value) return;
  open.value = false;
  if (restoreFocus) nextTick(() => triggerRef.value?.focus());
};

const toggleOpen = () => {
  if (props.disabled) return;
  if (open.value) {
    closeListbox({ restoreFocus: true });
  } else {
    openListbox();
  }
};

const selectOption = opt => {
  if (opt.disabled) return;
  const prev = props.modelValue;
  // Escolher a opção JÁ ativa não é mudança: só fecha a lista. Emitindo
  // `change` aqui, reescolher "Ganho" num card já ganho pedia ao servidor um
  // fechamento que ele recusa por antifraude (409) — e a tela mostrava um erro
  // genérico para uma ação que nunca poderia dar certo.
  if (opt.value === prev) {
    closeListbox({ restoreFocus: true });
    return;
  }
  emit('update:modelValue', opt.value);
  emit('change', opt.value, prev);
  closeListbox({ restoreFocus: true });
};

const onKeydown = e => {
  if (e.key !== 'Escape' || !open.value) return;
  e.preventDefault?.();
  closeListbox({ restoreFocus: true });
};

const onTriggerKeydown = event => {
  if (props.disabled) return;

  if (['Enter', ' ', 'ArrowDown', 'ArrowUp'].includes(event.key)) {
    event.preventDefault();
    openListbox(event.key === 'ArrowUp' ? 'last' : 'selected');
  }
};

const onListboxKeydown = event => {
  const options = enabledOptionElements();
  const activeIndex = options.indexOf(document.activeElement);

  if (event.key === 'Escape') {
    event.preventDefault();
    event.stopPropagation();
    closeListbox({ restoreFocus: true });
    return;
  }

  if (event.key === 'Tab') {
    closeListbox();
    return;
  }

  if (!options.length) return;

  if (['ArrowDown', 'ArrowUp', 'Home', 'End'].includes(event.key)) {
    event.preventDefault();
    let nextIndex = activeIndex;

    if (event.key === 'Home') nextIndex = 0;
    if (event.key === 'End') nextIndex = options.length - 1;
    if (event.key === 'ArrowDown') {
      nextIndex = activeIndex < 0 ? 0 : (activeIndex + 1) % options.length;
    }
    if (event.key === 'ArrowUp') {
      nextIndex = activeIndex <= 0 ? options.length - 1 : activeIndex - 1;
    }

    options[nextIndex]?.focus();
    return;
  }

  if (event.key === 'Enter' || event.key === ' ') {
    event.preventDefault();
    const optionIndex = optionRefs.value.indexOf(document.activeElement);
    const option = props.options[optionIndex];
    if (option) selectOption(option);
  }
};

// ── Click-outside ────────────────────────────────────────────────────────────

const onDocumentClick = e => {
  if (!open.value) return;
  if (pillRef.value && !pillRef.value.contains(e.target)) {
    closeListbox();
  }
};

watch(
  () => props.disabled,
  disabled => {
    if (disabled) closeListbox();
  }
);

onMounted(() => document.addEventListener('click', onDocumentClick, true));
onBeforeUnmount(() =>
  document.removeEventListener('click', onDocumentClick, true)
);

// Expose for testing
defineExpose({ open, onKeydown, selectOption, toggleOpen, openListbox });
</script>

<template>
  <div ref="pillRef" class="relative inline-flex">
    <!-- ── Pill trigger ── -->
    <button
      :id="triggerId"
      ref="triggerRef"
      type="button"
      :aria-expanded="open"
      aria-haspopup="listbox"
      :aria-controls="listboxId"
      :disabled="disabled"
      class="inline-flex min-h-11 items-center gap-1.5 rounded-full px-3 py-1 text-xs font-medium cursor-pointer transition-opacity duration-100 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand focus-visible:ring-offset-2 disabled:opacity-50 disabled:cursor-default"
      :class="pillColorClass"
      @click.stop="toggleOpen"
      @keydown="onTriggerKeydown"
    >
      <!-- Icon slot -->
      <span
        v-if="selectedOption && selectedOption.icon"
        class="flex-shrink-0 text-current"
        aria-hidden="true"
      >
        <component
          :is="selectedOption.icon"
          v-if="typeof selectedOption.icon !== 'string'"
        />
        <span v-else>{{ selectedOption.icon }}</span>
      </span>

      <span>{{ pillLabel }}</span>

      <!-- Chevron -->
      <svg
        xmlns="http://www.w3.org/2000/svg"
        width="12"
        height="12"
        viewBox="0 0 24 24"
        fill="none"
        stroke="currentColor"
        stroke-width="2.5"
        stroke-linecap="round"
        stroke-linejoin="round"
        class="flex-shrink-0 transition-transform duration-150"
        :class="open ? 'rotate-180' : ''"
        aria-hidden="true"
      >
        <polyline points="6 9 12 15 18 9" />
      </svg>
    </button>

    <!-- ── Popover ── -->
    <div
      v-if="open"
      :id="listboxId"
      role="listbox"
      :aria-labelledby="triggerId"
      class="absolute z-50 bg-n-solid-1 border border-n-weak rounded-lg shadow-lg p-1 min-w-[160px] transition-all duration-100"
      :class="popoverClass"
      @keydown="onListboxKeydown"
    >
      <button
        v-for="(opt, index) in options"
        :key="opt.value"
        :ref="element => (optionRefs[index] = element)"
        role="option"
        type="button"
        tabindex="-1"
        :aria-selected="opt.value === modelValue"
        :disabled="opt.disabled"
        class="w-full min-h-11 flex items-center gap-2 px-3 py-2 rounded text-sm text-n-slate-12 hover:bg-n-alpha-1 cursor-pointer transition-colors duration-75 text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-n-brand disabled:opacity-50 disabled:cursor-not-allowed"
        :class="opt.value === modelValue ? 'bg-n-alpha-1 font-medium' : ''"
        @click.stop="selectOption(opt)"
      >
        <!-- Option color dot -->
        <span
          v-if="opt.colorClass"
          class="inline-block w-2 h-2 rounded-full flex-shrink-0"
          :class="opt.colorClass"
          aria-hidden="true"
        />

        <!-- Option icon -->
        <span
          v-if="opt.icon && !opt.colorClass"
          class="flex-shrink-0 text-n-slate-11"
          aria-hidden="true"
        >
          {{ opt.icon }}
        </span>

        <span class="flex-1">{{ opt.label }}</span>

        <!-- Checkmark for selected -->
        <svg
          v-if="opt.value === modelValue"
          xmlns="http://www.w3.org/2000/svg"
          width="12"
          height="12"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          stroke-width="2.5"
          stroke-linecap="round"
          stroke-linejoin="round"
          class="text-n-brand flex-shrink-0"
          aria-hidden="true"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      </button>
    </div>
  </div>
</template>
